import React from "react";

export interface ConfigImageSource {
  url: string;
  alt?: string;
  file_format?:
    "image/png" | "image/jpg" | "image/webp" | "image/svg" | "image/avif";
  width?: number; // Intrinsic width for layout shift prevention
  height?: number; // Intrinsic height for layout shift prevention
  media?: string; // Media query for this source
  sizes?: string; // Sizes attribute for srcset
}

export interface ConfigImageProps {
  sources: ConfigImageSource[];
  className?: string;
}

// Format priority for fallback (higher priority first)
const FORMAT_PRIORITY: Record<string, number> = {
  avif: 4,
  webp: 3,
  jpg: 2,
  jpeg: 2,
  png: 1,
  svg: 0,
};

interface GroupedSource {
  media?: string;
  sources: ConfigImageSource[];
}

/**
 * ConfigImage Component
 *
 * Renders responsive images with format fallback using the <picture> element.
 * Follows web.dev best practices for responsive images.
 *
 * Features:
 * - Format fallback (avif → webp → jpg/png)
 * - Responsive breakpoints with media queries
 * - Proper srcset for size variants
 * - Prevents layout shift with width/height
 */
const ConfigImage: React.FC<ConfigImageProps> = ({
  sources,
  className = "",
}) => {
  if (!sources || sources.length === 0) {
    return null;
  }

  // Group sources by media query
  const groupedByMedia = sources.reduce<GroupedSource[]>((acc, source) => {
    const mediaQuery = source.media || "default";
    let group = acc.find((g) => (g.media || "default") === mediaQuery);

    if (!group) {
      group = { media: source.media, sources: [] };
      acc.push(group);
    }

    group.sources.push(source);
    return acc;
  }, []);

  // Sort groups: specific media queries first, default last
  groupedByMedia.sort((a, b) => {
    if (!a.media) return 1;
    if (!b.media) return -1;
    return 0;
  });

  // Find fallback image (lowest priority format without media query)
  // This ensures higher-priority formats become <source> elements that browsers check first
  const sourcesWithoutMedia = sources.filter((s) => !s.media);
  const fallbackSource =
    sourcesWithoutMedia.length > 0
      ? sourcesWithoutMedia.reduce((lowest, current) => {
          const lowestFormat = lowest.file_format?.replace("image/", "") || "";
          const currentFormat =
            current.file_format?.replace("image/", "") || "";
          const lowestPriority = FORMAT_PRIORITY[lowestFormat] || 0;
          const currentPriority = FORMAT_PRIORITY[currentFormat] || 0;
          return currentPriority < lowestPriority ? current : lowest;
        })
      : sources[sources.length - 1];
  const alt = sources.find((s) => s.alt)?.alt || "";

  // Get dimensions from any source that has them
  const dimensionSource = sources.find((s) => s.width && s.height);
  const width = dimensionSource?.width;
  const height = dimensionSource?.height;

  return (
    <picture className="flex justify-center">
      {groupedByMedia.map((group, groupIndex) => {
        // Sort sources by format priority within each group
        const sortedSources = [...group.sources].sort((a, b) => {
          const formatA = a.file_format?.replace("image/", "") || "";
          const formatB = b.file_format?.replace("image/", "") || "";
          const priorityA = FORMAT_PRIORITY[formatA] || 0;
          const priorityB = FORMAT_PRIORITY[formatB] || 0;
          return priorityB - priorityA;
        });

        return (
          <React.Fragment key={groupIndex}>
            {sortedSources.map((source, sourceIndex) => {
              // Skip the fallback source in <source> elements (it goes in <img>)
              if (source === fallbackSource) {
                return null;
              }

              const type = source.file_format
                ? `${source.file_format === "image/jpg" ? "image/jpeg" : source.file_format}`
                : undefined;

              return (
                <source
                  key={`${groupIndex}-${sourceIndex}`}
                  srcSet={source.url}
                  type={type}
                  media={group.media}
                  sizes={source.sizes}
                />
              );
            })}
          </React.Fragment>
        );
      })}

      <img
        src={fallbackSource.url}
        alt={alt}
        width={width}
        height={height}
        loading="eager"
        decoding="async"
        className={`h-auto max-w-full ${className}`}
      />
    </picture>
  );
};

export default ConfigImage;
